import React, { useState } from 'react';
import {
  Stack,
  TextInput,
  Button,
  Text,
  Alert,
  Group,
  Tooltip,
  useMantineColorScheme,
  Box
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  IconMail,
  IconPhone,
  IconAlertCircle,
  IconArrowLeft,
  IconUserPlus,
  IconCheck,
  IconX,
  IconInfoCircle
} from '@tabler/icons-react';

import registroTemporalService from '../../services/registroTemporalService';
import { registroService } from '../../services/registroService';
import { 
  Paso1DatosPersonales,
  Paso2Contacto
} from '../../types/registro';
import { getGlassmorphismInputStyles, glassmorphismInputProps } from '../../styles/inputStyles';
import { 
  normalizePhoneNumber, 
  validatePhoneNumber, 
  formatPhoneForDisplay,
  DEFAULT_COUNTRY 
} from '../../utils/phoneUtils';

interface Step2ContactProps {
  paso1Data: Paso1DatosPersonales;
  onComplete: (data: Paso2Contacto, sessionToken: string) => void;
  onBack: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const Step2Contact: React.FC<Step2ContactProps> = ({
  paso1Data,
  onComplete,
  onBack,
  loading,
  setLoading
}) => {
  const { colorScheme } = useMantineColorScheme();
  const [phoneValue, setPhoneValue] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');

  const form = useForm<{ email: string; telefono: string }>({
    initialValues: {
      email: '',
      telefono: ''
    },
    validateInputOnBlur: true,
    validate: {
      email: (value) => {
        if (!value) return 'El email es obligatorio';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Formato de email inválido';
        return null;
      },
      telefono: (value) => {
        // Validación usando las utilidades de teléfono
        if (!value) return 'El teléfono es obligatorio';
        const normalized = normalizePhoneNumber(value, DEFAULT_COUNTRY);
        if (!normalized) return 'Formato de teléfono inválido';
        if (!validatePhoneNumber(value, DEFAULT_COUNTRY)) return 'Número de teléfono inválido';
        return null;
      }
    }
  });

  const handleSubmit = async (values: { email: string; telefono: string }) => {
    // Validar teléfono antes de enviar
    const normalizedPhone = normalizePhoneNumber(phoneValue, DEFAULT_COUNTRY);
    if (!normalizedPhone) {
      setPhoneError('Formato de teléfono inválido');
      return;
    }

    setLoading(true);
    setPhoneError('');

    try {
      // 🎆 FORZAR REGISTRO TEMPORAL SIEMPRE
      
      // Inicializar sesión temporal si no existe
      if (!registroTemporalService.hasActiveSession()) {
        console.log('🚀 Iniciando sesión temporal desde Step2...');
        await registroTemporalService.iniciarSesionTemporal();
      }
      
      if (registroTemporalService.hasActiveSession()) {
        
        // Primero guardar paso 1 si no se ha guardado
        try {
          await registroTemporalService.guardarPaso1(paso1Data);
        } catch (error) {
          console.warn('Error guardando paso 1, posiblemente ya guardado:', error);
        }
        
        // Guardar paso 2 y enviar códigos de verificación
        const response = await registroTemporalService.guardarPaso2({
          email: values.email.toLowerCase().trim(),
          telefono: normalizedPhone
        });
        
        if (response.success) {
          notifications.show({
            title: 'Código enviado',
            message: 'Se ha enviado un código de verificación a tu email',
            color: 'blue',
            icon: <IconCheck size={16} />
          });
          
          // Combinar datos para el wizard
          const datosCompletos: Paso2Contacto = {
            ...paso1Data,
            email: values.email.toLowerCase().trim(),
            telefono: normalizedPhone
          };
          
          // Pasar el token de la sesión temporal
          const sessionToken = registroTemporalService.getSessionToken();
          onComplete(datosCompletos, sessionToken || 'TEMP_SESSION');
          return;
        } else {
          throw new Error(response.mensaje || 'Error al guardar datos');
        }
      }
      
      // FALLBACK: Si no hay sesión temporal, usar modo wizard solamente
      notifications.show({
        title: 'Datos guardados',
        message: 'Información guardada. Completa todos los pasos para crear tu cuenta.',
        color: 'blue',
        icon: <IconCheck size={16} />
      });

      // Combinar datos para el wizard (NO crear cuenta aún)
      const datosCompletos: Paso2Contacto = {
        ...paso1Data,
        email: values.email.toLowerCase().trim(),
        telefono: normalizedPhone
      };

      // Pasar token especial "WIZARD_ONLY" para indicar modo temporal local
      onComplete(datosCompletos, 'WIZARD_ONLY');
      return;
    } catch (error: any) {
      console.error('Error saving step 2 data:', error);
      let errorMessage = 'Error al guardar datos';
      if (error.response?.data?.mensaje) {
        errorMessage = error.response.data.mensaje;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (value: string | undefined) => {
    const phoneValue = value || '';
    setPhoneValue(phoneValue);
    form.setFieldValue('telefono', phoneValue);
    setPhoneError('');
    
    // Validar en tiempo real
    if (phoneValue && !validatePhoneNumber(phoneValue, DEFAULT_COUNTRY)) {
      setPhoneError('Formato de teléfono inválido');
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        
        {/* Resumen de datos del paso anterior */}
        <Alert color="blue" variant="light">
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={500}>Resumen de tus datos:</Text>
            </Group>
            <Group gap="sm" wrap="wrap">
              <Text size="sm">
                <strong>{paso1Data.nombre} {paso1Data.apellidos}</strong>
              </Text>
              <Text size="sm" c="dimmed">• @{paso1Data.username}</Text>
              <Text size="sm" c="dimmed">• {paso1Data.paisNacimiento}</Text>
              <Text size="sm" c="dimmed">• {paso1Data.genero === 'M' ? 'Masculino' : paso1Data.genero === 'F' ? 'Femenino' : 'Otro'}</Text>
              <Text size="sm" c="dimmed">• {new Date(paso1Data.fechaNacimiento).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })}</Text>
            </Group>
          </Stack>
        </Alert>

        {/* Información de contacto */}
        <Stack gap="md">
          <Group gap="xs">
            <Text size="sm" fw={600} c="dimmed">Información de contacto</Text>
            <Tooltip
              label="Enviaremos un código de verificación a tu email. El teléfono se almacenará como medio de contacto pero no requiere verificación."
              withArrow
              multiline
              w={250}
            >
              <IconInfoCircle size={14} style={{ opacity: 0.6, cursor: 'help' }} />
            </Tooltip>
          </Group>
          
          <TextInput
            placeholder="Correo electrónico (ej. tu@email.com)"
            leftSection={<IconMail size={16} />}
            required
            {...glassmorphismInputProps}
            styles={getGlassmorphismInputStyles(colorScheme)}
            {...form.getInputProps('email')}
          />

          {/* Selector de teléfono internacional */}
          <Box>
            <Text size="sm" fw={500} mb={5} c={phoneError ? 'red' : 'dimmed'}>
              Teléfono
            </Text>
            <PhoneInput
              placeholder="Selecciona país y escribe tu número"
              value={phoneValue}
              onChange={handlePhoneChange}
              defaultCountry={DEFAULT_COUNTRY}
              international
              countryCallingCodeEditable={false}
              style={{
                '--PhoneInputCountryFlag-height': '1em',
                '--PhoneInputCountryFlag-borderColor': 'transparent',
                '--PhoneInput-color--focus': colorScheme === 'dark' ? '#fff' : '#000',
                '--PhoneInput-background': 'transparent',
                '--PhoneInputInternationalIconPhone-opacity': '0.8',
              }}
              className={`phone-input ${colorScheme === 'dark' ? 'phone-input-dark' : 'phone-input-light'}`}
            />
            {phoneError && (
              <Text size="sm" c="red" mt={5}>
                {phoneError}
              </Text>
            )}
            {phoneValue && validatePhoneNumber(phoneValue, DEFAULT_COUNTRY) && (
              <Text size="xs" c="dimmed" mt={5}>
                {formatPhoneForDisplay(normalizePhoneNumber(phoneValue, DEFAULT_COUNTRY) || '')}
              </Text>
            )}
          </Box>
        </Stack>

        {/* Botones */}
        <Group justify="space-between" mt="md">
          <Button
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={onBack}
            disabled={loading}
          >
            Volver
          </Button>
          
          <Button
            type="submit"
            size="lg"
            loading={loading}
            disabled={!form.isValid()}
            leftSection={<IconCheck size={16} />}
            style={{ flex: 1, marginLeft: '1rem' }}
          >
            {loading ? 'Guardando...' : 'Guardar & Continuar'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default Step2Contact;